import { useEffect, useRef, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Paper,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/system";
import Grid from "@mui/material/Grid";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./RestaurantOwnerRegister.module.css";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/Clear";
import Visibility from "@mui/icons-material/Visibility";
import Dropdown1 from "../../Dropdown/Dropdown1";
import {
  useLoaderContext,
  useToastContext,
} from "../../../../Context/ToastContext";
import ViewDocument from "../../ViewDocument/ViewDocument";
import { useUserContext } from "../../../../Context/UserContext";
import { Helmet } from "react-helmet";
import { apiGet, apiPost, apiPut } from "../../../../utils/ApiManager";

const fetchDropdownCountries = process.env.REACT_APP_FETCH_DROPDOWN_COUNTRIES;
const fetchDropdownStates = process.env.REACT_APP_FETCH_DROPDOWN_STATES;
const fetchDropdownCities = process.env.REACT_APP_FETCH_DROPDOWN_CITIES;
const postHandleRegister = process.env.REACT_APP_POST_HANDLE_REGISTER;
const getOwnerDetails = process.env.REACT_APP_GET_OWNER_DETAILS;

// Custom styled Paper for form sections
const SectionPaper = styled(Paper)({
  padding: "20px",
  marginBottom: "20px",
});

const RestaurantOwnerRegister = ({ prev }) => {
  const { restaurantID } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    restaurantID: restaurantID,
    email: "",
    firstName: "",
    lastName: "",
    phoneNo: "",
    password: "",
    confirmPassword: "",
    panCardNo: "",
    panDocument: null,
    panFile: null,
    aadhaarCardNo: "",
    aadhaarDocument: null,
    aadhaarFile: null,
    addressLine: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
  });
  const [showDocument, setShowDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const { showToast } = useToastContext();
  const { setLoading } = useLoaderContext();
  const panFileInputRef = useRef(null);
  const aadhaarFileInputRef = useRef(null);

  const { requestConfirmation, getToken } = useUserContext();

  const handleClick = (inputRef) => {
    if (inputRef?.current) {
      inputRef.current.value = null;
    }
  };

  const handleViewDocument = (docUrl) => {
    setDocumentUrl(docUrl);
    setShowDocument(true);
  };

  const handleCloseDocument = () => {
    setDocumentUrl("");
    setShowDocument(false);
  };

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await apiGet(fetchDropdownCountries);
      if (response?.success) {
        setCountries(response?.data || []);
      } else {
        showToast(response.message || "Error fetching countries", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Error fetching countries",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (formData.country) {
      // Fetch states based on selected country
      const fetchStates = async () => {
        try {
          const response = await apiGet(
            `${fetchDropdownStates}=${formData.country}`
          );
          if (response?.success) {
            setStates(response.data || []);
          } else {
            showToast(response.message || "Error fetching states", "error");
          }
        } catch (error) {
          showToast(
            error?.response?.message ||
              error?.message ||
              "Error fetching states",
            "error"
          );
        } finally {
          setLoading(false);
        }
      };
      fetchStates();
    } else {
      setFormData({ ...formData, state: "", city: "" }); // Clear state and city when country is deselected
      setStates([]);
      setCities([]);
    }
  }, [formData.country]);

  useEffect(() => {
    if (formData.state) {
      // Fetch cities based on selected state
      const fetchCities = async () => {
        try {
          const response = await apiGet(
            `${fetchDropdownCities}=${formData.state}&country=${formData.country}`
          );
          if (response?.success) {
            setCities(response?.data || []);
          } else {
            showToast(response.message || "Error fetching cities", "error");
          }
        } catch (error) {
          showToast(
            error?.response?.message ||
              error?.message ||
              "Error fetching cities",
            "error"
          );
        } finally {
          setLoading(false);
        }
      };
      fetchCities();
    } else {
      setFormData({ ...formData, city: "" });
      setCities([]);
    }
  }, [formData.state]);

  useEffect(() => {
    if (restaurantID) {
      getOwnerData();
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Iterate over all formData keys and check if any value is empty
    Object.keys(formData).forEach((key) => {
      if (["panFile", "aadhaarFile"].includes(key)) {
        return;
      }
      if (key === "panDocument" && formData.panFile) {
        return;
      }
      if (key === "aadhaarDocument" && formData.aadhaarFile) {
        return;
      }
      if (["confirmPassword", "password"].includes(key) && formData.userId) {
        return;
      }

      if (!formData[key]) {
        // If any field in formData is empty
        newErrors[key] = "This field is required"; // Set the generic error message
        isValid = false; // Set isValid to false
      }
    });

    // Email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Phone Number
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.phoneNo) {
      newErrors.phoneNo = "Phone number is required";
      isValid = false;
    } else if (!phoneRegex.test(formData.phoneNo)) {
      newErrors.phoneNo = "Phone number must be exactly 10 digits";
      isValid = false;
    }

    // Validate Password
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

    if (!formData.password && !formData.userId) {
      newErrors.password = "This field is required.";
      isValid = false;
    } else if (!passwordRegex.test(formData.password) && !formData.userId) {
      newErrors.password =
        "Password must be at least 8 characters long, contain a capital letter, a number, and a special character.";
      isValid = false;
    }

    // Validate Confirm Password
    if (!formData.confirmPassword && !formData.userId) {
      newErrors.confirmPassword = "This field is required.";
      isValid = false;
    } else if (
      formData.password !== formData.confirmPassword &&
      !formData.userId
    ) {
      newErrors.confirmPassword = "Passwords do not match.";
      isValid = false;
    }

    //PAN
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!formData.panCardNo) {
      newErrors.panCardNo = "PAN is required";
      isValid = false;
    } else if (
      formData.panCardNo.length !== 10 ||
      !panRegex.test(formData.panCardNo)
    ) {
      newErrors.panCardNo =
        "Valid format of 10 characters (uppercase e.g., ABCDE0123R)";
      isValid = false;
    }

    //PAN Document
    if (formData.panDocument && formData.panDocument.size > 300 * 1024) {
      newErrors.panDocument = "File size must be less than 300KB";
      isValid = false;
    }

    //Aadhaar
    const aadhaarRegex = /^[0-9]{12}$/;
    if (!formData.aadhaarCardNo) {
      newErrors.aadhaarCardNo = "Aadhaar number is required";
      isValid = false;
    } else if (!aadhaarRegex.test(formData.aadhaarCardNo)) {
      newErrors.aadhaarCardNo = "Aadhar number must be exactly 12 digits";
      isValid = false;
    }

    //Aadhaar Document
    if (
      formData.aadhaarDocument &&
      formData.aadhaarDocument.size > 300 * 1024
    ) {
      newErrors.aadhaarDocument = "File size must be less than 300KB";
      isValid = false;
    }

    // Validate Pincode
    if (!formData.pincode) {
      newErrors.pincode = "Pincode is required";
    } else if (formData.pincode < 110000 || formData.pincode > 855999) {
      newErrors.pincode = "Pincode must be between 110000 and 855999";
      isValid = false;
    }
    setErrors(newErrors);
    setIsButtonDisabled(!isValid);

    return isValid;
  };

  useEffect(() => {
    const isValid = validateForm();
    setIsButtonDisabled(!isValid);
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value, // Update formData with the new input value
    }));

    // Clear any errors associated with this field
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
    validateForm();
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileSizeInKB = file.size / 1024;

    if (fileSizeInKB > 300) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "File size must be less than 300 KB.",
      }));
      return;
    }

    if (file.type !== "application/pdf") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "Only PDF files are allowed.",
      }));
      return;
    }

    // Generate preview URL
    const baseName = fieldName.replace("Document", ""); // pan or aadhaar
    const previewKey = `${baseName}File`;

    // Clean up old blob URL if exists
    if (formData[previewKey]) {
      URL.revokeObjectURL(formData[previewKey]);
    }

    const previewURL = URL.createObjectURL(file);

    // Update form data
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: file,
      [previewKey]: previewURL,
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: "",
    }));
  };

  const clearFile = (fieldName) => {
    const baseName = fieldName.replace("Document", ""); // pan or aadhaar
    const previewKey = `${baseName}File`;

    if (formData[previewKey]) {
      URL.revokeObjectURL(formData[previewKey]); // clean up
    }

    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: null,
      [previewKey]: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // const result = await requestConfirmation(
    //   "Want to register owner for restaurant?"
    // );
    // if (!result) return;

    setErrors({});

    const isValid = validateForm(); // Ensure this returns true/false and sets errors

    if (!isValid) return; // Stop if validation fails

    try {
      if (formData.userId) {
        await handleUpdate();
      } else {
        await handleRegister(); // assuming handleRegister returns response with restaurantId
      }
    } catch (error) {}
  };

  const handleRegister = async () => {
    try {
      setLoading(true);
      const result = await requestConfirmation("Want to register new Owner ?");
      if (!result) return;
      const response = await apiPost(postHandleRegister, formData, true);
      if (response.success) {
        showToast(response.message || "Owner Registered successful", "success");
        navigate(`/restaurants`);
      } else {
        showToast(
          response?.message || "An error occurred. Please try again.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message || "An error occurred. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const getOwnerData = async () => {
    try {
      setLoading(true);

      const response = await apiGet(`${getOwnerDetails}/${restaurantID}`);
      if (response.success) {
        const ownerDetails = response?.data;
        setFormData({
          restaurantID: restaurantID,
          userId: ownerDetails.userId || null,
          email: ownerDetails?.email || "",
          firstName: ownerDetails?.firstName || "",
          lastName: ownerDetails?.lastName || "",
          phoneNo: ownerDetails?.phoneNo || "",
          panCardNo: ownerDetails?.panCardNo || "",
          panDocument: null,
          panFile: ownerDetails?.panDocument || null,
          aadhaarCardNo: ownerDetails?.aadhaarCardNo || "",
          aadhaarDocument: null,
          aadhaarFile: ownerDetails?.aadhaarDocument || null,
          addressLine: ownerDetails.address?.addressLine || "",
          pincode: ownerDetails.address?.pincode || "",
          city: ownerDetails.address?.city || "",
          state: ownerDetails.address?.state || "",
          country: ownerDetails.address?.country || "",
        });
      } else {
        // showToast(response?.data?.message || 'An error occurred. Please try again.', 'info');
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "An error occurred. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    const result = await requestConfirmation(
      "Want to update owner for restaurant?"
    );

    if (!result) return;
    try {
      setLoading(true);
      const response = await apiPut(
        `${getOwnerDetails}/${restaurantID}`,
        formData,
        true
      );
      if (response.success) {
        showToast(
          response.message || "Owner Details updated successfully",
          "success"
        );
        navigate(`/restaurants`);
      } else {
        showToast(
          response.message || "An error occurred. Please try again.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "An error occurred. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={styles.mainProfilePage}>
      <Helmet>
        <title>Register Restaurant Owner | DiNEST</title>
        <meta
          name="description"
          content="Register restaurant owner on DiNEST"
        />
      </Helmet>
      <Box className={styles.mainContainer}>
        <Typography variant="h4" className={styles.containerTitle}>
          Restaurant Owner Registration
        </Typography>

        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Personal Information
            </Typography>

            <Grid container spacing={2}>
              <Grid className={styles.gridInput}>
                <TextField
                  label="First Name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  name="firstName"
                  value={formData.firstName}
                  className={styles.inputTextfield}
                  onChange={handleInputChange}
                  required
                />
                {errors.firstName && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.firstName}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="Last Name"
                  fullWidth
                  type="text"
                  variant="outlined"
                  name="lastName"
                  value={formData.lastName}
                  className={styles.inputTextfield}
                  onChange={handleInputChange}
                  required
                />
                {errors.lastName && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.lastName}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="Phone Number"
                  fullWidth
                  type="number"
                  variant="outlined"
                  name="phoneNo"
                  value={formData.phoneNo}
                  className={styles.inputTextfield}
                  onChange={handleInputChange}
                  required
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                    classes: { input: styles.noArrows },
                  }}
                />
                {errors.phoneNo && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.phoneNo}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  name="email"
                  value={formData.email}
                  className={styles.inputTextfield}
                  onChange={handleInputChange}
                  required
                />
                {errors.email && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.email}
                  </p>
                )}
              </Grid>
            </Grid>
          </SectionPaper>

          {/* Documents Upload */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Documents
            </Typography>

            <Grid
              container
              spacing={2}
              className={styles.documentSectionContainer}
            >
              <Grid className={styles.gridInputPancard}>
                <TextField
                  label="PAN Card"
                  name="panCardNo"
                  fullWidth
                  required
                  type="text"
                  className={styles.inputTextfield}
                  value={formData.panCardNo || ""} // Ensure that the value is controlled
                  onChange={handleInputChange}
                />
                {errors.panCardNo && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.panCardNo}
                  </p>
                )}
              </Grid>

              <Grid className={styles.uploadPanContainer}>
                <Grid container direction="column">
                  <Typography
                    variant="body1"
                    className={styles.uploadImageTitle}
                  >
                    Upload PAN Document
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (PDF &lt; 300kb)
                  </Typography>

                  {/* File Upload Button */}
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    className={styles.uploadButton}
                    onClick={handleClick}
                    startIcon={<CloudUploadIcon />}
                  >
                    Upload
                    <input
                      type="file"
                      hidden
                      name="panDocument"
                      ref={panFileInputRef} // FILE REF
                      onChange={(e) => handleFileChange(e, "panDocument")}
                      accept="application/pdf"
                    />
                  </Button>
                  {errors.panDocument && (
                    <p className={styles.errorUpload}>{errors.panDocument}</p>
                  )}

                  {/* Preview or Icon */}
                  {formData.panDocument && (
                    <Box display="flex" justifyContent="center">
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{
                            display: "inline-block",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            maxWidth: 120,
                            cursor: "pointer",
                          }}
                        >
                          {formData.panDocument?.name}
                        </Typography>
                        <IconButton onClick={() => clearFile("panDocument")}>
                          <ClearIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={2}
              className={styles.documentSectionContainer}
            >
              <Grid className={styles.gridInputPancard}>
                <TextField
                  label="Aadhaar Card"
                  name="aadhaarCardNo"
                  fullWidth
                  required
                  className={styles.inputTextfield}
                  type="number"
                  value={formData.aadhaarCardNo || ""} // Ensure that the value is controlled
                  onChange={handleInputChange}
                />
                {errors.aadhaarCardNo && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.aadhaarCardNo}
                  </p>
                )}
              </Grid>

              <Grid className={styles.uploadPanContainer}>
                <Grid container direction="column">
                  <Typography
                    variant="body1"
                    className={styles.uploadImageTitle}
                  >
                    Upload AADHAAR Document
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (PDF &lt; 300kb)
                  </Typography>
                  {/* File Upload Button */}
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    className={styles.uploadButton}
                    onClick={handleClick}
                    startIcon={<CloudUploadIcon />}
                  >
                    Upload
                    <input
                      type="file"
                      hidden
                      name="aadhaarDocument"
                      ref={aadhaarFileInputRef} // FILE REF
                      onChange={(e) => handleFileChange(e, "aadhaarDocument")}
                      accept="application/pdf"
                    />
                  </Button>
                  {errors.aadhaarDocument && (
                    <p
                      className={styles.errorUpload}
                      style={{ minHeight: "1.5rem" }}
                    >
                      {errors.aadhaarDocument}
                    </p>
                  )}

                  {/* Preview or Icon */}
                  {formData.aadhaarDocument && (
                    <Box display="flex" justifyContent="center">
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="caption"
                          sx={{
                            display: "inline-block",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            maxWidth: 120,
                            cursor: "pointer",
                          }}
                        >
                          {formData.aadhaarDocument?.name}
                        </Typography>
                        <IconButton
                          onClick={() => clearFile("aadhaarDocument")}
                        >
                          <ClearIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </SectionPaper>

          {/* Address Information */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Address Information
            </Typography>

            <Grid container spacing={2}>
              <Grid className={styles.gridInputAddress}>
                <TextField
                  label="Address Line"
                  fullWidth
                  type="text"
                  variant="outlined"
                  name="addressLine"
                  value={formData.addressLine}
                  className={styles.inputBoxAddress}
                  onChange={handleInputChange}
                  required
                />
                {errors.addressLine && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.addressLine}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="Pincode"
                  fullWidth
                  type="number"
                  variant="outlined"
                  name="pincode"
                  className={styles.inputTextfield}
                  value={formData.pincode}
                  onChange={handleInputChange}
                  required
                />
                {errors.pincode && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.pincode}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <Dropdown1
                  label={"Country"}
                  name={"country"}
                  options={countries}
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                />
                {errors.country && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.country}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <Dropdown1
                  label={"State"}
                  name={"state"}
                  options={states}
                  value={formData.state}
                  onChange={handleInputChange}
                />
                {errors.state && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.state}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <Dropdown1
                  label="City"
                  fullWidth
                  variant="outlined"
                  name="city"
                  value={formData.city}
                  options={cities}
                  onChange={handleInputChange}
                  required
                />
                {errors.city && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.city}
                  </p>
                )}
              </Grid>
            </Grid>
          </SectionPaper>

          {/* Password Fields */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Password Information
            </Typography>

            <Grid container spacing={2}>
              <Grid className={styles.gridInput}>
                <TextField
                  label="Password"
                  fullWidth
                  type="password"
                  className={styles.inputTextfield}
                  variant="outlined"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  // required
                />
                {errors.password && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.password}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="Confirm Password"
                  fullWidth
                  type="password"
                  className={styles.inputTextfield}
                  variant="outlined"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  // required
                />
                {errors.confirmPassword && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.confirmPassword}
                  </p>
                )}
              </Grid>
            </Grid>
          </SectionPaper>

          {/* Submit Button */}
          <Box textAlign="center">
            <Button
              variant="contained"
              className={styles.prevButton}
              onClick={() => prev(restaurantID)}
              sx={{ marginRight: 2, backgroundColor: "#207d46" }}
            >
              Prev
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={styles.saveButton}
              disabled={isButtonDisabled}
              sx={{ backgroundColor: "#207d46" }}
            >
              {formData?.userId ? "Update" : "Register"}
            </Button>
          </Box>
        </form>
      </Box>
      <ViewDocument
        isOpen={showDocument}
        onClose={handleCloseDocument}
        documentUrl={documentUrl}
      />
    </Box>
  );
};

export default RestaurantOwnerRegister;
